import React, { useState, useMemo } from 'react';
import { useAppData } from '@/context/AppContext';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import AppointmentSummaryModal from '@/components/AppointmentSummaryModal';
import { Activity, Zap, Thermometer, Plus, Trash2, Calendar, Search, Shield, TrendingUp, Award, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Clipboard } from 'lucide-react';
import styles from './DashboardPage.module.css';

function getTodayString() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function getDayIndex(): number {
  const jsDay = new Date().getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
}

function painColor(level: number) {
  if (level <= 3) return styles.painLow;
  if (level <= 6) return styles.painMed;
  return styles.painHigh;
}

function painHex(level: number) {
  if (level <= 3) return '#2E7D32';
  if (level <= 6) return '#E65100';
  return '#B71C1C';
}

const RECOVERY_STAGES = [
  { name: 'Diagnosis', shortName: 'Diagnosis', icon: Search, color: '#D8A7A0', description: 'Initial assessment & imaging' },
  { name: 'Protection', shortName: 'Protection', icon: Shield, color: '#E6D3A3', description: 'Rest & protect the injury' },
  { name: 'Rehab', shortName: 'Rehab', icon: TrendingUp, color: '#A8C3A0', description: 'Active rehabilitation' },
  { name: 'Return to Sport', shortName: 'Return', icon: Award, color: '#2F6F4F', description: 'Gradual return to activity' },
];

const DEFAULT_STAGE_WEEKS = [2, 4, 4, 4];
const PAIN_LOCATIONS = ['General', 'Lower Leg', 'Shin', 'Foot', 'Ankle', 'Knee', 'Hip', 'Back', 'Arm', 'Other'];

function parseStageWeeks(raw: string | undefined): number[] {
  try {
    if (!raw) return [...DEFAULT_STAGE_WEEKS];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length === 4) return parsed.map(Number);
  } catch {}
  return [...DEFAULT_STAGE_WEEKS];
}

function parseStageHistory(raw: string | undefined): { stage: number; date: string }[] {
  try {
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {}
  return [];
}

function parseManualStage(raw: string | undefined): number {
  const n = parseInt(raw ?? '0', 10);
  if (isNaN(n) || n < 0 || n > 3) return 0;
  return n;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function DashboardPage() {
  const {
    painLogs,
    prescribedExercises,
    exerciseCompletions,
    meals,
    appointments,
    settings,
    addPainLog,
    deleteAppointment,
    addAppointment,
    setSetting,
  } = useAppData();

  const [painLevel, setPainLevel] = useState(5);
  const [painLocation, setPainLocation] = useState('General');
  const [painNotes, setPainNotes] = useState('');
  const [painStep, setPainStep] = useState<1 | 2>(1);
  const [showPainModal, setShowPainModal] = useState(false);
  const [submittingPain, setSubmittingPain] = useState(false);

  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [apptForm, setApptForm] = useState({ title: '', date: '', time: '', location: '', notes: '' });
  const [submittingAppt, setSubmittingAppt] = useState(false);
  const [summaryAppt, setSummaryAppt] = useState<any | null>(null);
  const [showPastAppts, setShowPastAppts] = useState(false);

  const openPainModal = () => {
    setPainLevel(5);
    setPainLocation('General');
    setPainNotes('');
    setPainStep(1);
    setShowPainModal(true);
  };

  const closePainModal = () => {
    setShowPainModal(false);
    setPainStep(1);
  };

  const today = getTodayString();
  const todayDayIndex = getDayIndex();

  // Recovery stage
  const manualRecoveryStage = parseManualStage(settings.manualRecoveryStage);
  const stageWeeks = parseStageWeeks(settings.stageWeeks);
  const stageHistory = parseStageHistory(settings.stageHistory);
  const recoveryStartDate = settings.recoveryStartDate ? new Date(settings.recoveryStartDate) : new Date();
  const injuryType = settings.injuryType || '';
  const fractureLocation = settings.fractureLocation || '';
  const userName = settings.userName || '';

  const daysInRecovery = Math.max(0, Math.floor(
    (Date.now() - recoveryStartDate.getTime()) / (1000 * 60 * 60 * 24)
  ));

  const stageStartEntry = [...stageHistory].reverse().find(e => e.stage === manualRecoveryStage);
  const estimatedStageStart = (() => {
    const d = new Date(recoveryStartDate);
    for (let i = 0; i < manualRecoveryStage; i++) {
      d.setDate(d.getDate() + (stageWeeks[i] ?? 4) * 7);
    }
    return d;
  })();
  const isLegacyDate = stageStartEntry && manualRecoveryStage > 0 &&
    Math.abs(new Date(stageStartEntry.date).getTime() - recoveryStartDate.getTime()) < 2 * 24 * 60 * 60 * 1000;
  const stageStartDate = (!stageStartEntry || isLegacyDate) ? estimatedStageStart : new Date(stageStartEntry.date);
  const daysInCurrentStage = Math.max(0, Math.floor((Date.now() - stageStartDate.getTime()) / (1000 * 60 * 60 * 24)));
  const totalStageWeeks = stageWeeks[manualRecoveryStage] ?? 4;
  const weeksInStage = Math.floor(daysInCurrentStage / 7) + 1;
  const stageProgressFraction = Math.min(daysInCurrentStage / (totalStageWeeks * 7), 1);

  const currentStage = RECOVERY_STAGES[manualRecoveryStage];
  const nextStage = manualRecoveryStage < RECOVERY_STAGES.length - 1 ? RECOVERY_STAGES[manualRecoveryStage + 1] : null;

  const injuryLabel = injuryType && injuryType !== 'Stress Fracture'
    ? injuryType
    : fractureLocation
      ? `${fractureLocation.split(' ')[0].toLowerCase()} fracture`
      : null;

  const handleAdvance = async () => {
    if (manualRecoveryStage >= 3) return;
    const idx = manualRecoveryStage + 1;
    const newHistory = [...stageHistory, { stage: idx, date: new Date().toISOString() }];
    await setSetting('manualRecoveryStage', String(idx));
    await setSetting('stageHistory', JSON.stringify(newHistory));
  };

  const handleGoBack = async () => {
    if (manualRecoveryStage <= 0) return;
    await setSetting('manualRecoveryStage', String(manualRecoveryStage - 1));
  };

  // Daily stats
  const latestPain = useMemo(() => {
    if (painLogs.length === 0) return null;
    return [...painLogs].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
  }, [painLogs]);

  const exercisesDoneToday = useMemo(() => {
    const todayCompletions = exerciseCompletions.filter(c => c.date === today);
    const prescribedToday = prescribedExercises.filter(e => e.scheduleDays.includes(todayDayIndex));
    return { done: todayCompletions.length, total: prescribedToday.length };
  }, [exerciseCompletions, prescribedExercises, today, todayDayIndex]);

  const proteinToday = useMemo(() => {
    return meals
      .filter(m => m.timestamp.toISOString().slice(0, 10) === today)
      .reduce((sum: number, m: any) => sum + (m.protein || 0), 0);
  }, [meals, today]);

  const upcomingAppointments = useMemo(() => {
    const now = new Date();
    return [...appointments].filter(a => a.date >= now).sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [appointments]);

  const pastAppointments = useMemo(() => {
    const now = new Date();
    return [...appointments].filter(a => a.date < now).sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [appointments]);

  const handlePainSubmit = async () => {
    setSubmittingPain(true);
    try {
      await addPainLog(painLevel, painLocation, painNotes);
      setPainNotes('');
      closePainModal();
    } finally {
      setSubmittingPain(false);
    }
  };

  const handleApptSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apptForm.title || !apptForm.date) return;
    setSubmittingAppt(true);
    try {
      const dateStr = apptForm.time
        ? new Date(`${apptForm.date}T${apptForm.time}`).toISOString()
        : new Date(`${apptForm.date}T09:00`).toISOString();
      await addAppointment(apptForm.title, dateStr, apptForm.location, apptForm.notes);
      setApptForm({ title: '', date: '', time: '', location: '', notes: '' });
      setShowAppointmentModal(false);
    } finally {
      setSubmittingAppt(false);
    }
  };

  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className={styles.container}>

      {/* ── Greeting ── */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>
            {getGreeting()}{userName ? `, ${userName.split(' ')[0]}` : ''}
          </h1>
          <p className={styles.pageSubtitle}>{dateLabel}</p>
        </div>
        <span className={styles.dateBadge}>Day {daysInRecovery}</span>
      </div>

      {/* ── Stats Strip ── */}
      <div className={styles.statsStrip}>
        <div className={styles.statChip}>
          <div className={styles.statDot} style={{ background: latestPain ? painHex(latestPain.level) + '22' : 'var(--border)' }}>
            <Thermometer size={14} color={latestPain ? painHex(latestPain.level) : 'var(--text-secondary)'} />
          </div>
          <div>
            <div className={styles.statLabel}>Pain</div>
            <div className={`${styles.statValue} ${latestPain ? painColor(latestPain.level) : styles.statEmpty}`}>
              {latestPain ? `${latestPain.level}/10` : '—'}
            </div>
          </div>
        </div>
        <div className={styles.statChip}>
          <div className={styles.statDot} style={{ background: 'rgba(217,138,99,0.15)' }}>
            <Activity size={14} color="var(--secondary)" />
          </div>
          <div>
            <div className={styles.statLabel}>Exercises</div>
            <div className={styles.statValue} style={{ color: 'var(--secondary)' }}>
              {exercisesDoneToday.done}/{exercisesDoneToday.total}
            </div>
          </div>
        </div>
        <div className={styles.statChip}>
          <div className={styles.statDot} style={{ background: 'rgba(217,138,99,0.12)' }}>
            <Zap size={14} color="var(--secondary)" />
          </div>
          <div>
            <div className={styles.statLabel}>Protein</div>
            <div className={styles.statValue} style={{ color: 'var(--secondary)' }}>{proteinToday}g</div>
          </div>
        </div>
      </div>

      {/* ── Recovery Stage ── */}
      <div className={styles.section}>
        <Card>
          <div className={styles.stageRow}>
            <div className={styles.stageIconWrap} style={{ background: currentStage.color + '22' }}>
              {React.createElement(currentStage.icon, { size: 20, color: currentStage.color })}
            </div>
            <div style={{ flex: 1 }}>
              <div className={styles.stageEyebrow}>Current Stage</div>
              <div className={styles.stageName} style={{ color: currentStage.color }}>{currentStage.name}</div>
              <div className={styles.stageDesc}>{currentStage.description}</div>
            </div>
            {injuryLabel ? (
              <span className={styles.injuryPill}>{injuryLabel}</span>
            ) : null}
          </div>

          <div className={styles.progressMeta}>
            <span>Week {Math.min(weeksInStage, totalStageWeeks)} of {totalStageWeeks}</span>
            <span style={{ color: currentStage.color, fontWeight: 700 }}>
              {Math.round(stageProgressFraction * 100)}%
            </span>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{
                width: `${Math.max(stageProgressFraction * 100, 2)}%`,
                background: `linear-gradient(to right, ${currentStage.color}, ${currentStage.color}99)`,
              }}
            />
          </div>

          <div className={styles.stageNavRow}>
            <button
              className={styles.stageNavBtn}
              onClick={handleGoBack}
              disabled={manualRecoveryStage === 0}
              style={manualRecoveryStage === 0 ? { opacity: 0.3 } : undefined}
            >
              <ChevronLeft size={14} /> Back
            </button>
            {nextStage ? (
              <button
                className={styles.stageNavBtnPrimary}
                onClick={handleAdvance}
                style={{ background: currentStage.color }}
              >
                Advance to {nextStage.shortName} <ChevronRight size={14} />
              </button>
            ) : (
              <div className={styles.stageComplete} style={{ color: currentStage.color, borderColor: currentStage.color + '40', background: currentStage.color + '10' }}>
                Final stage — great work!
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── Pain Check-in ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Pain Check-in</h2>
        </div>
        <Card>
          <div className={styles.painCheckin}>
            {latestPain ? (
              <div className={styles.painLastRow}>
                <div>
                  <div className={styles.painLastLabel}>Last logged</div>
                  <div className={styles.painLastMeta}>
                    {latestPain.timestamp.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                    {latestPain.location && latestPain.location !== 'General' ? ` · ${latestPain.location}` : ''}
                  </div>
                </div>
                <span className={`${styles.painLastScore} ${painColor(latestPain.level)}`}>
                  {latestPain.level}<span className={styles.painLastOf}>/10</span>
                </span>
              </div>
            ) : (
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
                No pain logged yet — tap below to get started.
              </p>
            )}
            <button className={styles.submitBtn} onClick={openPainModal}>Log Pain</button>
          </div>
        </Card>
      </div>

      {/* ── Pain Log Modal ── */}
      <Modal isOpen={showPainModal} onClose={closePainModal} title={painStep === 1 ? "How's your pain today?" : 'Add details'}>
        {painStep === 1 ? (
          <div className={styles.painStep1}>
            <div className={styles.painScoreDisplay} style={{ color: painLevel <= 3 ? '#2E7D32' : painLevel <= 6 ? '#E65100' : '#B71C1C' }}>
              {painLevel}<span className={styles.painScoreOf}>/10</span>
            </div>
            <div className={styles.painScoreDesc}>
              {painLevel <= 2 ? 'Minimal — barely noticeable' :
               painLevel <= 4 ? 'Mild — noticeable but manageable' :
               painLevel <= 6 ? 'Moderate — interfering with activity' :
               painLevel <= 8 ? 'Severe — significantly limiting' :
               'Worst pain imaginable'}
            </div>
            <div className={styles.painScoreGrid}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => {
                const col = n <= 3 ? '#2E7D32' : n <= 5 ? '#E65100' : n <= 7 ? '#FF7043' : '#B71C1C';
                const active = painLevel === n;
                return (
                  <button
                    key={n}
                    className={styles.painScoreBtn}
                    style={{
                      background: active ? col : 'var(--bg-secondary)',
                      color: active ? '#fff' : col,
                      borderColor: col,
                      fontWeight: active ? 700 : 500,
                      transform: active ? 'scale(1.15)' : 'scale(1)',
                    }}
                    onClick={() => setPainLevel(n)}
                  >{n}</button>
                );
              })}
            </div>
            <div className={styles.painScaleLabels}><span>No pain</span><span>Worst pain</span></div>
            <button className={styles.submitBtn} onClick={() => setPainStep(2)}>Continue — Add Details</button>
          </div>
        ) : (
          <div className={styles.painStep2}>
            <div className={styles.painDetailHeader}>
              <span className={styles.painDetailBadge} style={{ background: painLevel <= 3 ? '#2E7D3220' : painLevel <= 6 ? '#E6510020' : '#B71C1C20', color: painLevel <= 3 ? '#2E7D32' : painLevel <= 6 ? '#E65100' : '#B71C1C' }}>
                Pain score: {painLevel}/10
              </span>
              <button className={styles.painBackBtn} onClick={() => setPainStep(1)}>← Change score</button>
            </div>
            <div>
              <label className={styles.painFieldLabel}>Where is the pain?</label>
              <div className={styles.locationGrid}>
                {PAIN_LOCATIONS.map(loc => (
                  <button
                    key={loc}
                    type="button"
                    className={`${styles.locationBtn} ${painLocation === loc ? styles.locationBtnActive : ''}`}
                    onClick={() => setPainLocation(loc)}
                  >{loc}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={styles.painFieldLabel}>Notes <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
              <textarea
                value={painNotes}
                onChange={e => setPainNotes(e.target.value)}
                placeholder="What were you doing? Any triggers or observations..."
                rows={3}
                className={styles.painTextarea}
              />
            </div>
            <button className={styles.submitBtn} onClick={handlePainSubmit} disabled={submittingPain}>
              {submittingPain ? 'Logging...' : 'Log Pain'}
            </button>
          </div>
        )}
      </Modal>

      {/* ── Appointments ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Upcoming Appointments</h2>
          <button className={styles.addBtn} onClick={() => setShowAppointmentModal(true)}>
            <Plus size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Add
          </button>
        </div>

        {upcomingAppointments.length === 0 ? (
          <Card>
            <p className={styles.emptyText}>No upcoming appointments</p>
          </Card>
        ) : (
          <div className={styles.appointmentList}>
            {upcomingAppointments.map(appt => (
              <div key={appt.id} className={styles.appointmentItem}>
                <div className={styles.appointmentItemTop}>
                  <div className={styles.appointmentInfo}>
                    <span className={styles.appointmentTitle}>{appt.title}</span>
                    <span className={styles.appointmentMeta}>
                      <Calendar size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {appt.date.toLocaleDateString()} at {appt.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {appt.location ? ` · ${appt.location}` : ''}
                    </span>
                    {appt.notes ? <span className={styles.appointmentNotes}>{appt.notes}</span> : null}
                  </div>
                  <button className={styles.deleteBtn} onClick={() => deleteAppointment(appt.id)}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <button className={styles.summaryBtn} onClick={() => setSummaryAppt(appt)}>
                  <Clipboard size={13} /> Prepare summary
                </button>
              </div>
            ))}
          </div>
        )}

        {pastAppointments.length > 0 ? (
          <div className={styles.pastApptsWrap}>
            <button className={styles.pastApptsToggle} onClick={() => setShowPastAppts(p => !p)}>
              {showPastAppts ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              Past appointments ({pastAppointments.length})
            </button>
            {showPastAppts ? (
              <div className={`${styles.appointmentList} ${styles.pastApptsList}`}>
                {pastAppointments.map(appt => (
                  <div key={appt.id} className={`${styles.appointmentItem} ${styles.pastApptItem}`}>
                    <div className={styles.appointmentItemTop}>
                      <div className={styles.appointmentInfo}>
                        <span className={styles.appointmentTitle}>{appt.title}</span>
                        <span className={styles.appointmentMeta}>
                          <Calendar size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          {appt.date.toLocaleDateString()} at {appt.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <button className={styles.deleteBtn} onClick={() => deleteAppointment(appt.id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* ── Add Appointment Modal ── */}
      <Modal isOpen={showAppointmentModal} onClose={() => setShowAppointmentModal(false)} title="Add Appointment">
        <form onSubmit={handleApptSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Title</label>
            <input className={styles.formInput} value={apptForm.title} onChange={e => setApptForm(p => ({ ...p, title: e.target.value }))} placeholder="Appointment title" required />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Date</label>
            <input type="date" className={styles.formInput} value={apptForm.date} onChange={e => setApptForm(p => ({ ...p, date: e.target.value }))} required />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Time</label>
            <input type="time" className={styles.formInput} value={apptForm.time} onChange={e => setApptForm(p => ({ ...p, time: e.target.value }))} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Location</label>
            <input className={styles.formInput} value={apptForm.location} onChange={e => setApptForm(p => ({ ...p, location: e.target.value }))} placeholder="Location (optional)" />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Notes</label>
            <textarea className={styles.formTextarea} value={apptForm.notes} onChange={e => setApptForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)" />
          </div>
          <button type="submit" className={styles.formSubmitBtn} disabled={submittingAppt}>
            {submittingAppt ? 'Adding...' : 'Add Appointment'}
          </button>
        </form>
      </Modal>

      {summaryAppt !== null && (
        <AppointmentSummaryModal appointment={summaryAppt} onClose={() => setSummaryAppt(null)} />
      )}
    </div>
  );
}
